import React, { ReactNode, useState, useMemo } from 'react'
import { Button } from '../Button'

export interface Column<T> {
  key: string
  header: string
  render?: (item: T, index: number) => ReactNode
  sortable?: boolean
  className?: string
  headerClassName?: string
  searchable?: boolean
  editable?: boolean
  onEdit?: (item: T, newValue: any) => void
}

export interface TableAction<T> {
  label: string | ((item: T) => string)
  onClick: (item: T) => void
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  disabled?: (item: T) => boolean
  loading?: (item: T) => boolean
  className?: string
}

export interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  actions?: TableAction<T>[]
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  selectable?: boolean
  selectedItems?: Set<string>
  onSelectionChange?: (selectedIds: Set<string>) => void
  getItemId?: (item: T) => string
  pagination?: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
    onPageChange: (page: number) => void
  }
  className?: string
  searchable?: boolean
  sortable?: boolean
  title?: string
}

export function DataTable<T>({
  data,
  columns,
  actions,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  selectable = false,
  selectedItems = new Set(),
  onSelectionChange,
  getItemId = (item: any) => item.id,
  pagination,
  className = "",
  searchable = true,
  sortable = true,
  title
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [editingCell, setEditingCell] = useState<{rowId: string, columnKey: string} | null>(null)
  const [editValue, setEditValue] = useState<string>('')

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...data]

    // Apply search filter
    if (searchable && searchTerm) {
      filtered = filtered.filter(item => {
        return columns.some(column => {
          if (column.searchable === false) return false
          const value = String((item as any)[column.key] || '')
          return value.toLowerCase().includes(searchTerm.toLowerCase())
        })
      })
    }

    // Apply sorting
    if (sortable && sortField) {
      filtered.sort((a, b) => {
        const aValue = String((a as any)[sortField] || '')
        const bValue = String((b as any)[sortField] || '')

        if (sortDirection === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      })
    }

    return filtered
  }, [data, searchTerm, sortField, sortDirection, searchable, sortable, columns])

  const handleSort = (columnKey: string) => {
    if (!sortable) return

    if (sortField === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(columnKey)
      setSortDirection('asc')
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return

    if (checked) {
      const allIds = new Set(filteredAndSortedData.map(getItemId))
      onSelectionChange(allIds)
    } else {
      onSelectionChange(new Set())
    }
  }

  const handleSelectItem = (item: T) => {
    if (!onSelectionChange) return

    const id = getItemId(item)
    const newSelection = new Set(selectedItems)

    if (newSelection.has(id)) {
      newSelection.delete(id)
    } else {
      newSelection.add(id)
    }

    onSelectionChange(newSelection)
  }

  const handleEditStart = (item: T, columnKey: string) => {
    const itemId = getItemId(item)
    setEditingCell({rowId: itemId, columnKey})
    setEditValue(String((item as any)[columnKey] || ''))
  }

  const handleEditSave = (item: T, columnKey: string) => {
    const column = columns.find(col => col.key === columnKey)
    if (column?.onEdit) {
      column.onEdit(item, editValue)
    }
    setEditingCell(null)
    setEditValue('')
  }

  const handleEditCancel = () => {
    setEditingCell(null)
    setEditValue('')
  }

  if (loading) {
    return (
      <div className={`bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-2">⚠️</div>
            <div className="text-gray-900 font-medium mb-2">Error Loading Data</div>
            <div className="text-gray-600 text-sm">{error}</div>
          </div>
        </div>
      </div>
    )
  }

  if (filteredAndSortedData.length === 0 && !searchTerm) {
    return (
      <div className={`bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-gray-400 text-4xl mb-2">📄</div>
            <div className="text-gray-900 font-medium mb-2">No Data</div>
            <div className="text-gray-600 text-sm">{emptyMessage}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Header with title and search */}
      {(title || searchable) && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {title && (
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            )}
            {searchable && (
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              Showing {filteredAndSortedData.length} of {data.length} results for "{searchTerm}"
            </div>
          )}
        </div>
      )}

      <div className="table-container">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {selectable && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && data.every(item => selectedItems.has(getItemId(item)))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.headerClassName || ''} ${
                    column.sortable !== false && sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {column.sortable !== false && sortable && (
                      <span className="text-gray-400">
                        {sortField === column.key ? (
                          sortDirection === 'asc' ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions && actions.length > 0 && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0) + (actions?.length ? 1 : 0)} className="px-6 py-12 text-center">
                  <div className="text-gray-400 text-4xl mb-2">🔍</div>
                  <div className="text-gray-900 font-medium mb-2">No results found</div>
                  <div className="text-gray-600 text-sm">Try adjusting your search terms</div>
                </td>
              </tr>
            ) : (
              filteredAndSortedData.map((item, index) => {
                const itemId = getItemId(item)
                return (
                  <tr key={itemId} className="hover:bg-gray-50">
                    {selectable && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(itemId)}
                          onChange={() => handleSelectItem(item)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </td>
                    )}
                    {columns.map((column) => {
                      const isEditing = editingCell?.rowId === itemId && editingCell?.columnKey === column.key
                      return (
                        <td
                          key={column.key}
                          className={`px-6 py-4 whitespace-nowrap ${column.className || ''}`}
                        >
                          {isEditing ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditSave(item, column.key)
                                  } else if (e.key === 'Escape') {
                                    handleEditCancel()
                                  }
                                }}
                              />
                              <button
                                onClick={() => handleEditSave(item, column.key)}
                                className="text-green-600 hover:text-green-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={handleEditCancel}
                                className="text-red-600 hover:text-red-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div
                              className={`${column.editable ? 'table-cell-editable cursor-pointer rounded px-2 py-1' : ''}`}
                              onClick={() => column.editable && handleEditStart(item, column.key)}
                            >
                              {column.render
                                ? column.render(item, index)
                                : String((item as any)[column.key] || '-')
                              }
                              {column.editable && (
                                <span className="ml-2 text-gray-400 edit-icon">
                                  <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      )
                    })}
                    {actions && actions.length > 0 && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {actions.map((action, actionIndex) => (
                            <Button
                              key={actionIndex}
                              variant={action.variant || 'outline'}
                              size={action.size || 'sm'}
                              onClick={() => action.onClick(item)}
                              disabled={action.disabled?.(item) || action.loading?.(item)}
                              className={action.className}
                            >
                              {action.loading?.(item) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                              ) : (
                                typeof action.label === 'function' ? action.label(item) : action.label
                              )}
                            </Button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              variant="outline"
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.totalItems}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  className="rounded-r-none"
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1
                  } else if (pagination.currentPage <= 3) {
                    pageNum = i + 1
                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i
                  } else {
                    pageNum = pagination.currentPage - 2 + i
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === pagination.currentPage ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => pagination.onPageChange(pageNum)}
                      className="rounded-none"
                    >
                      {pageNum}
                    </Button>
                  )
                })}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="rounded-l-none"
                >
                  Next
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}